import type { ReactNode } from 'react'
import { Plus } from 'lucide-react'
import { Card } from '@klear/ui/components/card'
import { Button } from '@klear/ui/components/button'
import { Label } from '@klear/ui/components/label'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'

export type EntitySelectOrCreateProps = {
  selectTitle: string
  selectDescription: string
  selectFieldLabel: string
  options: ComboboxOption[]
  selectedValue: string
  onSelectedChange: (value: string) => void
  selectAside?: ReactNode
  onSelectConfirm: () => void
  selectConfirmLabel?: string
  selectConfirmDisabled?: boolean
  selectPlaceholder?: string
  selectTriggerLabel?: string
  isLoading?: boolean

  createTitle: string
  createDescription: string
  createContent?: ReactNode
  onCreateNew?: () => void
  createActionLabel?: string
  inlineCreate?: boolean
}

export function EntitySelectOrCreate({
  selectTitle,
  selectDescription,
  selectFieldLabel,
  options,
  selectedValue,
  onSelectedChange,
  selectAside,
  onSelectConfirm,
  selectConfirmLabel = 'Next',
  selectConfirmDisabled,
  selectPlaceholder,
  selectTriggerLabel,
  isLoading,
  createTitle,
  createDescription,
  createContent,
  onCreateNew,
  createActionLabel = 'Create new',
  inlineCreate = false
}: EntitySelectOrCreateProps) {
  const hasExisting = options.length > 0 || selectAside != null

  if (inlineCreate && onCreateNew != null && createContent == null) {
    if (!hasExisting) {
      return (
        <div className="space-y-6 w-full max-w-2xl">
          <Card className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-foreground">{createTitle}</h2>
            <p className="text-sm text-muted-foreground">{createDescription}</p>
            <div className="flex justify-end">
              <Button onClick={onCreateNew}>
                <Plus className="h-4 w-4 mr-1" />
                {createActionLabel}
              </Button>
            </div>
          </Card>
        </div>
      )
    }

    return (
      <div className="space-y-6 w-full max-w-2xl">
        <Card className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-foreground">{selectTitle}</h2>
          <p className="text-sm text-muted-foreground">{selectDescription}</p>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{selectFieldLabel}</Label>
            <Combobox
              options={options}
              value={selectedValue}
              onChange={onSelectedChange}
              disabled={isLoading}
              triggerLabel={isLoading ? 'Loading…' : (selectTriggerLabel ?? 'Select…')}
              placeholder={selectPlaceholder ?? 'Search…'}
            />
            {selectAside}
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={onSelectConfirm} disabled={selectConfirmDisabled}>
              {selectConfirmLabel}
            </Button>
            <Button variant="outline" onClick={onCreateNew}>
              <Plus className="h-4 w-4 mr-1" />
              {createActionLabel}
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 w-full max-w-2xl">
      {hasExisting ? (
        <Card className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-foreground">{selectTitle}</h2>
          <p className="text-sm text-muted-foreground">{selectDescription}</p>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{selectFieldLabel}</Label>
            <Combobox
              options={options}
              value={selectedValue}
              onChange={onSelectedChange}
              disabled={isLoading}
              triggerLabel={isLoading ? 'Loading…' : (selectTriggerLabel ?? 'Select…')}
              placeholder={selectPlaceholder ?? 'Search…'}
            />
            {selectAside}
          </div>
          <div className="flex justify-end">
            <Button onClick={onSelectConfirm} disabled={selectConfirmDisabled}>
              {selectConfirmLabel}
            </Button>
          </div>
        </Card>
      ) : null}

      <Card className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">{createTitle}</h2>
        <p className="text-sm text-muted-foreground">{createDescription}</p>
        {createContent ?? (
          <div className="flex justify-end">
            <Button variant={hasExisting ? 'outline' : 'default'} onClick={onCreateNew}>
              <Plus className="h-4 w-4 mr-1" />
              {createActionLabel}
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}
